import { useState, useEffect, useRef } from "react";
import { frontRegions, backRegions } from "../data/Coordinates";
import anatomyFront from "assets/incidentManagement/anatomy-front.png";
import anatomyBack from "assets/incidentManagement/anatomy-back.png";

const HumanBodyWrapper = ({ selectedParts = [], onPartsChange, error = false, compact = false }) => {
  const [activeView, setActiveView] = useState("front");
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [debugPoints, setDebugPoints] = useState([]);
  const [debugPath, setDebugPath] = useState("");
  const [debugShapeType, setDebugShapeType] = useState("polygon"); // polygon, rectangle, ellipse
  const [isDrawing, setIsDrawing] = useState(false);
  const [dragPointIndex, setDragPointIndex] = useState(null);
  const svgRef = useRef(null);

  // Normalize selectedParts to always be an array
  const selectedPartsArray = Array.isArray(selectedParts) ? selectedParts : (selectedParts ? [selectedParts] : []);

  // Get regions for current view
  const regions = activeView === "front" ? frontRegions : backRegions;

  // Get image for current view
  const currentImage = activeView === "front" ? anatomyFront : anatomyBack;

  // Handle region click - toggle selection
  const handleRegionClick = (regionId) => {
    const newSelectedParts = selectedPartsArray.includes(regionId)
      ? selectedPartsArray.filter((id) => id !== regionId)
      : [...selectedPartsArray, regionId];

    // Call the callback with the new selection
    if (onPartsChange) {
      onPartsChange(newSelectedParts);
    }
  };

  // Check if a region is selected
  const isSelected = (regionId) => {
    return selectedPartsArray.includes(regionId);
  };

  // Get selected region names for display
  const getSelectedNames = () => {
    const allRegions = [...frontRegions, ...backRegions];
    return selectedPartsArray
      .map((id) => {
        const region = allRegions.find((r) => r.id === id);
        return region ? region.name : null;
      })
      .filter(Boolean);
  };

  // Debug mode: Convert screen coordinates to SVG viewBox coordinates
  const getSVGPoint = (event) => {
    if (!svgRef.current) return null;

    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const viewBox = svg.viewBox.baseVal;

    // Calculate the scale factors
    const scaleX = viewBox.width / rect.width;
    const scaleY = viewBox.height / rect.height;

    // Get click position relative to SVG
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
  };

  // Sort points in clockwise order around the centroid to create a proper polygon
  const sortPointsClockwise = (points) => {
    if (points.length < 3) return points;

    // Calculate centroid
    const centroid = {
      x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
      y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
    };

    // Sort points by angle from centroid
    return [...points].sort((a, b) => {
      const angleA = Math.atan2(a.y - centroid.y, a.x - centroid.x);
      const angleB = Math.atan2(b.y - centroid.y, b.x - centroid.x);
      return angleA - angleB;
    });
  };

  // Generate path based on shape type
  const generatePathFromPoints = (points, shapeType = debugShapeType) => {
    if (points.length === 0) return "";

    if (shapeType === "rectangle" && points.length >= 2) {
      const minX = Math.min(...points.map(p => p.x));
      const maxX = Math.max(...points.map(p => p.x));
      const minY = Math.min(...points.map(p => p.y));
      const maxY = Math.max(...points.map(p => p.y));
      return `M ${minX} ${minY} L ${maxX} ${minY} L ${maxX} ${maxY} L ${minX} ${maxY} Z`;
    }

    if (shapeType === "ellipse" && points.length >= 2) {
      const minX = Math.min(...points.map(p => p.x));
      const maxX = Math.max(...points.map(p => p.x));
      const minY = Math.min(...points.map(p => p.y));
      const maxY = Math.max(...points.map(p => p.y));
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const rx = Math.abs(maxX - minX) / 2;
      const ry = Math.abs(maxY - minY) / 2;
      return `M ${cx} ${cy - ry} A ${rx} ${ry} 0 0 1 ${cx} ${cy + ry} A ${rx} ${ry} 0 0 1 ${cx} ${cy - ry} Z`;
    }

    // Polygon mode
    if (points.length === 1) {
      return `M ${points[0].x} ${points[0].y}`;
    }
    if (points.length === 2) {
      return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    }

    // Sort points for proper polygon shape
    const sortedPoints = sortPointsClockwise(points);
    const pathParts = [`M ${sortedPoints[0].x} ${sortedPoints[0].y}`];
    for (let i = 1; i < sortedPoints.length; i++) {
      pathParts.push(`L ${sortedPoints[i].x} ${sortedPoints[i].y}`);
    }
    pathParts.push("Z"); // Close path
    return pathParts.join(" ");
  };

  // Handle SVG click in debug mode
  const handleSVGClick = (event) => {
    if (!debugMode || dragPointIndex !== null) return;

    const point = getSVGPoint(event);
    if (!point) return;

    if (debugShapeType === "rectangle" || debugShapeType === "ellipse") {
      // For rectangle/ellipse, only need 2 points (start and end)
      if (debugPoints.length === 0) {
        setDebugPoints([point]);
        setIsDrawing(true);
      } else if (debugPoints.length === 1) {
        setDebugPoints([debugPoints[0], point]);
        setIsDrawing(false);
        const path = generatePathFromPoints([debugPoints[0], point], debugShapeType);
        setDebugPath(path);
      } else {
        // Reset and start new shape
        setDebugPoints([point]);
        setIsDrawing(true);
      }
    } else {
      // Polygon mode - add points
      const newPoints = [...debugPoints, point];
      setDebugPoints(newPoints);
      const path = generatePathFromPoints(newPoints, "polygon");
      setDebugPath(path);
    }
  };

  // Handle mouse move for rectangle/ellipse drawing
  const handleSVGMouseMove = (event) => {
    if (!debugMode || !isDrawing || debugPoints.length !== 1) return;

    const point = getSVGPoint(event);
    if (!point) return;

    const path = generatePathFromPoints([debugPoints[0], point], debugShapeType);
    setDebugPath(path);
  };

  // Handle mouse up to finish drawing
  const handleSVGMouseUp = () => {
    if (isDrawing && debugPoints.length === 1) {
      setIsDrawing(false);
    }
  };

  // Handle point dragging
  const handlePointMouseDown = (index, event) => {
    event.stopPropagation();
    setDragPointIndex(index);
  };

  const handleSVGMouseMoveDrag = (event) => {
    if (dragPointIndex === null) return;

    const point = getSVGPoint(event);
    if (!point) return;

    const newPoints = [...debugPoints];
    newPoints[dragPointIndex] = point;
    setDebugPoints(newPoints);
    const path = generatePathFromPoints(newPoints, debugShapeType);
    setDebugPath(path);
  };

  const handleSVGMouseUpDrag = () => {
    setDragPointIndex(null);
  };

  // Remove a point by index
  const removePoint = (index) => {
    const newPoints = debugPoints.filter((_, i) => i !== index);
    setDebugPoints(newPoints);
    const path = generatePathFromPoints(newPoints, debugShapeType);
    setDebugPath(path);
  };

  // Quick templates for common body parts
  const applyTemplate = (template) => {
    setDebugPoints(template.points);
    setDebugShapeType(template.type);
    const path = generatePathFromPoints(template.points, template.type);
    setDebugPath(path);
  };

  // Reset debug points
  const resetDebugPoints = () => {
    setDebugPoints([]);
    setDebugPath("");
    setIsDrawing(false);
    setDragPointIndex(null);
  };

  // Copy path to clipboard
  const copyPathToClipboard = () => {
    if (debugPath) {
      navigator.clipboard.writeText(debugPath);
      alert("Path copied to clipboard!");
    }
  };

  return (
    <div className={`w-full ${compact ? "" : "w-full"}`}>
      {/* View Toggle */}
      <div className="mb-4 flex gap-2 p-1 bg-gray-100 rounded-lg">
        <button
          type="button"
          onClick={() => setActiveView("front")}
          className={`flex-1 px-4 py-2 rounded-md font-medium text-sm transition-all duration-200 ${
            activeView === "front"
              ? "bg-white text-gray-900 shadow-md"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Front View
        </button>
        <button
          type="button"
          onClick={() => setActiveView("back")}
          className={`flex-1 px-4 py-2 rounded-md font-medium text-sm transition-all duration-200 ${
            activeView === "back"
              ? "bg-white text-gray-900 shadow-md"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Back View
        </button>
      </div>

      {/* Anatomy Image Container */}
      <div
        className={`relative ${compact ? "max-w-md mx-auto" : "w-full"} aspect-[100/185] bg-white rounded-lg shadow-lg border ${
          error ? "border-red-500" : "border-gray-200"
        } overflow-hidden`}
      >
        {/* Background Image */}
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <img
            src={currentImage}
            alt={`Human anatomy ${activeView} view`}
            className="max-w-full max-h-full object-contain"
            draggable={false}
          />
        </div>

        {/* SVG Overlay for clickable regions */}
        <svg
          ref={svgRef}
          className="absolute inset-0 w-full h-full cursor-pointer"
          viewBox="0 0 100 185"
          preserveAspectRatio="xMidYMid meet"
        >
          {regions.map((region) => {
            const selected = isSelected(region.id);
            const hovered = hoveredRegion === region.id;

            return (
              <path
                key={region.id}
                id={region.id}
                d={region.path}
                fill={
                  selected
                    ? "rgba(239, 68, 68, 0.5)"
                    : hovered
                    ? "rgba(156, 163, 175, 0.3)"
                    : "transparent"
                }
                stroke={selected ? "#EF4444" : hovered ? "#9CA3AF" : "transparent"}
                strokeWidth={selected ? "0.8" : "0.5"}
                className="cursor-pointer transition-all duration-150"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRegionClick(region.id);
                }}
                onMouseEnter={() => setHoveredRegion(region.id)}
                onMouseLeave={() => setHoveredRegion(null)}
              />
            );
          })}

        </svg>

        {/* Hover Tooltip */}
        {hoveredRegion && !isSelected(hoveredRegion) && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-md shadow-lg z-10 pointer-events-none">
            {regions.find((r) => r.id === hoveredRegion)?.name}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-2 text-sm text-red-600">Please select at least one body part</p>
      )}

      {/* Selected Regions Summary Box - Only show when NOT in compact mode */}
      {!compact && (
        <div className="mt-4 mb-4">
          {selectedPartsArray.length > 0 ? (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-blue-900">
                  Selected Regions ({selectedPartsArray.length})
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedPartsArray.map((partId) => {
                  const region = regions.find((r) => r.id === partId) ||
                    [...frontRegions, ...backRegions].find((r) => r.id === partId);
                  if (!region) return null;

                  return (
                    <button
                      key={partId}
                      type="button"
                      onClick={() => handleRegionClick(partId)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 rounded-lg text-sm text-blue-800 hover:bg-blue-100 hover:border-blue-400 transition-all duration-150 shadow-sm"
                    >
                      <span className="font-medium">{region.name}</span>
                      <svg
                        className="w-4 h-4 text-red-500 hover:text-red-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-500 italic">
                No body parts selected yet. Click on the diagram to indicate injury locations.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HumanBodyWrapper;
